import React, { useEffect, useMemo, useCallback } from "react";
import { Flame, Sun, Snowflake, PhoneCall, ShieldAlert } from "lucide-react";
import { useGeneric } from "../../hooks/useGeneric";
import { formatDate, formatAmcStatus, TEMPERATURE_ORDER } from "../../utils/crmHelpers";
import "../../styles/crm.css";

const TEMP_ICON = { hot: Flame, warm: Sun, cold: Snowflake };
const AMC_WINDOW_DAYS = 60;

const Overview = () => {
    const { data: leads = [], fetchAll: fetchLeads } = useGeneric({
        tableName: "crm_leads", idColumn: "lead_id",
    });
    const { data: schedule = [], fetchAll: fetchSchedule } = useGeneric({
        tableName: "crm_payment_schedule", idColumn: "schedule_id",
    });
    const { data: agencies = [], fetchAll: fetchAgencies } = useGeneric({
        tableName: "agencies", idColumn: "agency_id",
    });

    useEffect(() => { fetchLeads(); fetchSchedule(); fetchAgencies(); }, [fetchLeads, fetchSchedule, fetchAgencies]);

    const agencyName = useCallback(
        (id) => agencies.find((a) => Number(a.agency_id) === Number(id))?.agency_name ?? "—",
        [agencies]
    );

    // Merge: sales calls due (from leads) + payment follow-ups due (from schedule)
    const callQueue = useMemo(() => {
        const salesCalls = leads
            .filter((l) => ["lead", "in_progress"].includes(l.status) && l.next_call_date)
            .map((l) => ({
                key: `lead-${l.lead_id}`,
                date: l.next_call_date,
                name: l.lead_name,
                mobile: l.mobile_number,
                temperature: l.temperature || "warm",
                type: "Sales Call",
            }));

        const paymentCalls = schedule
            .filter((s) => !s.is_paid)
            .map((s) => ({
                key: `pay-${s.schedule_id}`,
                date: s.due_date,
                name: agencyName(s.client_id),
                mobile: "",
                temperature: "warm",
                type: `Payment Due (₹${s.amount})`,
            }));

        return [...salesCalls, ...paymentCalls].sort((a, b) => {
            const d = new Date(a.date) - new Date(b.date);
            if (d !== 0) return d;
            return (TEMPERATURE_ORDER[a.temperature] ?? 3) - (TEMPERATURE_ORDER[b.temperature] ?? 3);
        });
    }, [leads, schedule, agencyName]);

    const amcWatch = useMemo(() => {
        return agencies
            .map((a) => ({ ...a, amc: formatAmcStatus(a.amc_valid_upto) }))
            .filter((a) => a.amc_valid_upto && a.amc.urgency !== "none" &&
                (a.amc.urgency === "expired" ||
                    (new Date(a.amc_valid_upto) - new Date()) / 86400000 <= AMC_WINDOW_DAYS))
            .sort((a, b) => new Date(a.amc_valid_upto) - new Date(b.amc_valid_upto));
    }, [agencies]);

    return (
        <div className="crm-overview">
            <div className="crm-overview-card">
                <div className="crm-overview-header"><PhoneCall size={16} /> <h3>Upcoming Calls</h3></div>
                <div className="crm-overview-body">
                    {callQueue.length === 0 ? (
                        <div className="crm-empty-inline">Nothing due — you're all caught up.</div>
                    ) : callQueue.slice(0, 15).map((c) => {
                        const TempIcon = TEMP_ICON[c.temperature] || Sun;
                        const overdue = new Date(c.date) < new Date(new Date().toDateString());
                        return (
                            <div key={c.key} className="crm-queue-row">
                                <span className={`crm-queue-date${overdue ? " overdue" : ""}`}>{formatDate(c.date)}</span>
                                <span className={`crm-temp crm-temp-${c.temperature}`}><TempIcon size={13} /></span>
                                <span className="crm-queue-name">{c.name}{c.mobile && <span className="crm-subtext"> · {c.mobile}</span>}</span>
                                <span className="crm-queue-type">{c.type}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="crm-overview-card">
                <div className="crm-overview-header"><ShieldAlert size={16} /> <h3>AMC Expiry Watch</h3></div>
                <div className="crm-overview-body">
                    {amcWatch.length === 0 ? (
                        <div className="crm-empty-inline">No AMC renewals due in the next {AMC_WINDOW_DAYS} days.</div>
                    ) : amcWatch.slice(0, 15).map((a) => (
                        <div key={a.agency_id} className="crm-queue-row">
                            <span className={`crm-queue-date${a.amc.urgency === "expired" || a.amc.urgency === "critical" ? " overdue" : ""}`}>
                                {formatDate(a.amc_valid_upto)}
                            </span>
                            <span className="crm-queue-name">{a.agency_name}</span>
                            <span className={`crm-flag ${a.amc.urgency === "expired" || a.amc.urgency === "critical" ? "danger" : a.amc.urgency === "warning" ? "warn" : "ok"}`}>
                                {a.amc.text}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Overview;